import { Clock, MailX } from "lucide-react";

import { Badge } from "@/components/ui/badge";
import { DeliveryReportAction } from "@lytra/frontend-common/__generated__/graphql";
import { cn } from "@/lib/utils";

export interface DeliveryProblem {
  recipient: string;
  action: DeliveryReportAction;
  statusCode?: string | null;
  diagnostic?: string | null;
}

// Enhanced status codes (RFC 3463) an agent can act on, in plain German. Anything
// else falls back to the reporting server's own wording.
const STATUS_CODE_REASONS: Record<string, string> = {
  "5.1.1": "Die Empfängeradresse existiert nicht.",
  "5.1.10": "Die Empfängeradresse existiert nicht.",
  "5.1.2": "Die Domain der Empfängeradresse existiert nicht.",
  "5.1.0": "Die Empfängeradresse ist ungültig.",
  "5.1.3": "Die Empfängeradresse ist ungültig.",
  "5.2.1": "Das Postfach des Empfängers ist deaktiviert.",
  "5.2.2": "Das Postfach des Empfängers ist voll.",
  "4.2.2": "Das Postfach des Empfängers ist voll.",
  "5.2.3": "Die Nachricht ist für den Empfänger zu groß.",
  "5.3.4": "Die Nachricht ist für den Empfänger zu groß.",
  "5.7.1": "Der Empfangsserver hat die Nachricht abgelehnt.",
  "5.7.0": "Der Empfangsserver hat die Nachricht abgelehnt.",
  "5.4.1": "Der Empfangsserver hat die Nachricht abgelehnt.",
  "5.4.4": "Der Empfangsserver ist nicht erreichbar.",
  "4.4.1": "Der Empfangsserver antwortet nicht.",
  "4.4.7": "Die Zustellung dauert zu lange.",
};

/** The plain-German reason of a reported problem, else the reporting server's wording (ADR-0057). */
export function deliveryProblemReason(problem: DeliveryProblem): string | null {
  if (problem.statusCode && STATUS_CODE_REASONS[problem.statusCode]) {
    return STATUS_CODE_REASONS[problem.statusCode];
  }
  return problem.diagnostic ?? null;
}

function hasFailure(problems: DeliveryProblem[]): boolean {
  return problems.some((p) => p.action === DeliveryReportAction.Failed);
}

/** Header badge on a sent message a bounce was reported for (ADR-0057). */
export function DeliveryProblemBadge({
  problems,
}: {
  problems: DeliveryProblem[];
}) {
  if (problems.length === 0) return null;
  const failed = hasFailure(problems);
  return (
    <Badge
      variant={failed ? "destructive" : "outline"}
      className={cn(
        "ml-2 gap-1",
        !failed && "bg-warning-subtle text-ink border-ink-faint",
      )}
    >
      {failed ? <MailX className="h-3 w-3" /> : <Clock className="h-3 w-3" />}
      {failed ? "Nicht zugestellt" : "Zustellung verzögert"}
    </Badge>
  );
}

/** Per-recipient explanation shown above the content of a bounced message. */
export function DeliveryProblemNotice({
  problems,
}: {
  problems: DeliveryProblem[];
}) {
  if (problems.length === 0) return null;
  const failed = problems.filter(
    (p) => p.action === DeliveryReportAction.Failed,
  );
  const delayed = problems.filter(
    (p) => p.action === DeliveryReportAction.Delayed,
  );

  return (
    <div className="mb-3 space-y-2">
      {failed.length > 0 && (
        <div
          role="alert"
          className="rounded-lg border border-destructive/40 bg-destructive/5 p-3 text-sm"
        >
          <div className="flex items-center gap-2 font-semibold text-destructive">
            <MailX className="h-4 w-4 shrink-0" />
            Diese Nachricht wurde nicht zugestellt
          </div>
          <ul className="mt-1 space-y-1 pl-6 text-ink">
            {failed.map((p) => (
              <li key={p.recipient}>
                <span className="font-medium">{p.recipient}</span>
                {deliveryProblemReason(p) && (
                  <span className="text-ink-muted"> – {deliveryProblemReason(p)}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 pl-6 text-ink-muted">
            Der Kunde hat diese Antwort nicht erhalten. Prüfen Sie die Adresse
            und senden Sie die Antwort erneut.
          </p>
        </div>
      )}
      {delayed.length > 0 && (
        <div
          role="status"
          className="rounded-lg border border-ink-faint bg-warning-subtle p-3 text-sm text-ink"
        >
          <div className="flex items-center gap-2 font-semibold">
            <Clock className="h-4 w-4 shrink-0" />
            Zustellung verzögert
          </div>
          <ul className="mt-1 space-y-1 pl-6">
            {delayed.map((p) => (
              <li key={p.recipient}>
                <span className="font-medium">{p.recipient}</span>
                {deliveryProblemReason(p) && (
                  <span className="text-ink-muted"> – {deliveryProblemReason(p)}</span>
                )}
              </li>
            ))}
          </ul>
          <p className="mt-2 pl-6 text-ink-muted">
            Der Mailserver versucht es weiter. Schlägt die Zustellung endgültig
            fehl, wird das Ticket wieder geöffnet.
          </p>
        </div>
      )}
    </div>
  );
}
